import {
  Box,
  Button,
  Flex,
  IconButton,
  InlineAlert,
  InputText,
  Link,
  Modal,
  Table,
  Text,
  useToast,
} from '@driftt/dds'
import { faPenToSquare } from '@driftt/dds.icons/light/faPenToSquare'
import { faTrash } from '@driftt/dds.icons/light/faTrash'
import { pathOr } from 'ramda'
import React, { useEffect, useState } from 'react'
import { useMutation } from 'react-query'
import { ReactQueryDevtools } from 'react-query/devtools'
import spacetime from 'spacetime'

import { deleteContentVariable } from 'api/playbooks'
import { useContentVariables } from 'hooks/useContentVariables'
import { isEmptyOrNil } from 'utils/index'

import EditVariableModal from '../EditVariableModal'
import ContentVariableUsageModal from '../VariableUsageModal'

const VariablesList = ({
  createVariableModalOpen = false,
  toggleCreateVariableModal,
}) => {
  const toast = useToast()
  const [nameQuery, setNameQuery] = useState('')
  const [currentVariable, setCurrentVariable] = useState(null)
  const [currentUsageVariable, setCurrentUsageVariable] = useState(null)
  const [hasSeenNewBanner, setHasSeenNewBanner] = useState(false)
  const [usageModalOpen, setUsageModalOpen] = useState(false)
  const [currentVariableForDeletion, setCurrentVariableForDeletion] =
    useState(null)
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(null)
  const {
    isLoading,
    variables,
    tableStateChange,
    pagination,
    rowTotal,
    refreshTableState,
  } = useContentVariables({
    nameQuery,
  })

  const deleteVariable = useMutation((id) => deleteContentVariable(id), {
    onSuccess: () => {
      refreshTableState()
      toast({
        message: 'Successfully deleted your variable.',
        variant: 'success',
      })
    },
    onError: () => {
      toast({
        message:
          'Could not delete your variable, contact support if this continues.',
        variant: 'error',
      })
    },
  })

  useEffect(() => {
    if (!isEmptyOrNil(currentVariable) && !createVariableModalOpen) {
      toggleCreateVariableModal(true)
    }
    if (!isEmptyOrNil(currentUsageVariable) && !usageModalOpen) {
      setUsageModalOpen(true)
    }
  }, [
    currentVariable,
    createVariableModalOpen,
    currentUsageVariable,
    usageModalOpen,
    toggleCreateVariableModal,
  ])

  return (
    <Flex fullHeight fullWidth direction="column">
      {!hasSeenNewBanner && (
        <Box fullWidth css={{ paddingBottom: '$2' }}>
          <InlineAlert
            variant="new"
            title="Make your life easier with content variables!"
            description="You can now put a personalized placeholder into your playbooks and update the value everywhere in a few easy clicks."
            onClose={() => setHasSeenNewBanner(true)}
            dismissable={true}
          />
        </Box>
      )}
      <Flex
        direction="column"
        fullWidth
        gap={0}
        css={{
          boxShadow: '$1',
          background: '$gray2',
        }}
      >
        <Box css={{ padding: '$2', background: '$white' }} width="100%">
          <Flex alignItems="center" justifyContent="space-between" gap={0}>
            <InputText
              onChange={({ target }) => setNameQuery(target.value)}
              placeholder="Search variables"
              value={nameQuery}
            />
          </Flex>
        </Box>
        <Flex fullWidth css={{ overflow: 'auto' }}>
          <Table
            rows={variables?.pageRows ?? []}
            disableBoxShadow={true}
            height={'65vh'}
            columns={[
              {
                value: 'name',
                header: 'Variable name',
                size: 194,
                cell: (row) => (
                  <Box css={{ maxWidth: 194 }}>
                    <Link
                      onClick={() => {
                        setCurrentVariable(row)
                      }}
                      variant="inline"
                      truncate={true}
                    >
                      {row.name}
                    </Link>
                  </Box>
                ),
              },
              {
                value: 'content',
                header: 'Content',
                size: 194,
                cell: (row) => (
                  <Box css={{ maxWidth: 194 }}>
                    <Text
                      truncate={true}
                      title={row.content.replace(/(<([^>]+)>)/gi, '')}
                    >
                      {row.content.replace(/(<([^>]+)>)/gi, '')}
                    </Text>
                  </Box>
                ),
              },
              {
                value: 'updatedAt',
                header: 'Last updated',
                size: 146,
                cell: (row) => (
                  <>{spacetime(row.updatedAt).format('numeric-us')}</>
                ),
              },
              {
                id: 'usage_count',
                value: 'usageCount',
                header: 'Used in',
                size: 164,
                cell: (row) => {
                  return <>{`${row.usageCount} playbooks & blocks`}</>
                },
              },
              {
                id: 'actions',
                size: 164,
                render: (row) => (
                  <>
                    <Flex alignItems="center" justifyContent="flex-end">
                      <Link
                        onClick={() => setCurrentUsageVariable(row)}
                        variant="inline"
                      >{`View ${row.usageCount} instances`}</Link>
                      <Flex>
                        <IconButton
                          icon={faPenToSquare}
                          title="Edit variable"
                          onClick={() => {
                            setCurrentVariable(row)
                          }}
                        />
                        <IconButton
                          icon={faTrash}
                          title="Delete variable"
                          onClick={() => {
                            setShowDeleteConfirmationModal(true)
                            setCurrentVariableForDeletion(row)
                          }}
                          disabled={row.usageCount > 0}
                        />
                      </Flex>
                    </Flex>
                  </>
                ),
              },
            ]}
            isLoading={isLoading}
            emptyState={{
              minHeight: 600,
              title: 'Update content used across playbooks from a single place',
            }}
            onStateChange={tableStateChange}
            pagination={{
              defaultPagination: pagination,
              rowTotal: rowTotal,
            }}
          />
        </Flex>
      </Flex>
      {createVariableModalOpen && (
        <EditVariableModal
          variable={currentVariable}
          isNew={isEmptyOrNil(currentVariable)}
          isOpen={createVariableModalOpen}
          onClose={() => {
            refreshTableState()
            setCurrentVariable(null)
            toggleCreateVariableModal(false)
          }}
        />
      )}
      {usageModalOpen && (
        <ContentVariableUsageModal
          isOpen={usageModalOpen}
          variable={currentUsageVariable}
          onClose={() => {
            setCurrentUsageVariable(null)
            setUsageModalOpen(false)
          }}
        />
      )}
      {showDeleteConfirmationModal &&
        !isEmptyOrNil(currentVariableForDeletion) && (
          <Modal.Root
            open={showDeleteConfirmationModal}
            onClose={() => setShowDeleteConfirmationModal(false)}
          >
            <Modal.Dialog size="x-large" variant="warning">
              <Modal.Title title="Are you sure?" />
              <Modal.Content>
                <Text>{`Do you want to delete ${pathOr(
                  'this variable',
                  ['name'],
                  currentVariableForDeletion
                )}? Once deleted you can no longer use it in playbooks and blocks.`}</Text>
              </Modal.Content>
              <Modal.Actions>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowDeleteConfirmationModal(false)
                    setCurrentVariableForDeletion(null)
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    deleteVariable.mutate(currentVariableForDeletion.id)
                    setShowDeleteConfirmationModal(false)
                    setCurrentVariableForDeletion(null)
                  }}
                >
                  Delete
                </Button>
              </Modal.Actions>
            </Modal.Dialog>
          </Modal.Root>
        )}
      <ReactQueryDevtools />
    </Flex>
  )
}

export default VariablesList
